'use client'

import { useState } from 'react'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'

interface UseMediaQueryOptions {
  defaultValue?: boolean
  initializeWithValue?: boolean
}

const IS_SERVER = typeof window === 'undefined'

/**
 * Common breakpoint sizes (matching Tailwind defaults)
 */
export const BREAKPOINTS = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const

type BreakpointName = keyof typeof BREAKPOINTS

/**
 * Base hook for media queries
 */
export function useMediaQuery(
  query: string,
  { defaultValue = false, initializeWithValue = true }: UseMediaQueryOptions = {},
): boolean {
  const getMatches = (query: string): boolean => {
    if (IS_SERVER) {
      return defaultValue
    }
    return window.matchMedia(query).matches
  }

  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) {
      return getMatches(query)
    }
    return defaultValue
  })

  // Handles the change event of the media query.
  function handleChange() {
    setMatches(getMatches(query))
  }

  useIsomorphicLayoutEffect(() => {
    const matchMedia = window.matchMedia(query)

    // Triggered at the first client-side load and if query changes
    handleChange()

    // Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)

    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange)
    } else {
      matchMedia.addEventListener('change', handleChange)
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange)
      } else {
        matchMedia.removeEventListener('change', handleChange)
      }
    }
  }, [query])

  return matches
}

/**
 * Convenient hook for breakpoint queries.
 * Accepts either a breakpoint name (e.g., 'md') or a custom size (e.g., '768px').
 *
 * @example
 * ```tsx
 * const isMd = useBreakpoint('md') // Uses predefined 768px breakpoint
 * const isCustom = useBreakpoint('900px') // Uses custom size
 * ```
 */
export function useBreakpoint(
  breakpoint: BreakpointName,
  direction?: 'min' | 'max',
  options?: UseMediaQueryOptions,
): boolean
export function useBreakpoint(breakpoint: string, direction?: 'min' | 'max', options?: UseMediaQueryOptions): boolean
export function useBreakpoint(
  breakpoint: BreakpointName | string,
  direction: 'min' | 'max' = 'min',
  options?: UseMediaQueryOptions,
): boolean {
  const size = breakpoint in BREAKPOINTS ? BREAKPOINTS[breakpoint as BreakpointName] : breakpoint
  const query = `(${direction}-width: ${size})`
  return useMediaQuery(query, options)
}
